<template>
  <div class="hss" @focusin="onFocusIn" @focusout="onFocusOut">
    <multiselect
      :id="elementId"
      ref="ms"
      :value="null"
      :options="mergedOptions"
      :loading="loading"
      :internal-search="false"
      :searchable="active"
      :allow-empty="true"
      :disabled="disabled"
      :placeholder="placeholder"
      track-by="id"
      :custom-label="labelFor"
      select-label=""
      selected-label=""
      deselect-label=""
      @select="onSelect"
      @search-change="onSearch"
      @open="onOpen"
      @close="onClose"
    >
      <template slot="noResult">
        <span v-if="!loading">No horses found.</span>
      </template>
      <template slot="noOptions">Start typing to search...</template>
      <li v-if="loadingMore" slot="afterList" class="multiselect__element">
        <span class="multiselect__option text-muted">Loading more...</span>
      </li>
    </multiselect>

    <!-- Same markup/classes as the legacy @voerro tags-input badge (styled in
         _filter.scss / _badges.scss) so the selected chip looks identical. A real
         <button> (not an <i role="button">) so the click reliably reaches clear().
         @mousedown.prevent keeps the button from taking focus, so the click never
         raises a `focusin` that bubbles to `.hss` and re-opens the search dropdown
         (onFocusIn). onFocusIn also bails out when focus lands inside this chip. -->
    <div v-if="selectedOption" class="tags-input mt-1">
      <span class="badge">
        <button
          v-if="!disabled"
          type="button"
          class="tags-input-remove"
          aria-label="Remove"
          @mousedown.prevent
          @click.stop.prevent="clear"
        />
        <span class="tags-input-label">{{ labelFor(selectedOption) }}</span>
      </span>
    </div>
  </div>
</template>

<script>
import Multiselect from '@/components/AppMultiselect'
import horsesApi from '@/services/api/horses'

// Server-backed replacement for the legacy `<tags-input :existing-tags="{id: 'owner: name'}">`
// horse pickers. Same behaviour: type >= 2 chars, match horse OR owner name, archived horses
// included, and the picked horse shows as a removable chip BELOW the field (input stays empty).
// `value` / `input` keep the tags-input shape: an array with the id string, or [].
const MIN_CHARS = 2
const DEBOUNCE_MS = 300

export default {
  name: 'HorseSearchSelect',
  components: { Multiselect },
  props: {
    value: { type: Array, default: () => [] },
    elementId: { type: String, required: true },
    // Pre-resolved { id, name, owner_name } for the currently selected horse, supplied by the
    // parent's main request (e.g. event-details). When it matches `value`, the chip renders
    // from it and no /v2/horses/options lookup is fired. Search stays per-field, on typing.
    initialOption: { type: Object, default: null },
    placeholder: { type: String, default: 'Start typing...' },
    disabled: Boolean,
    required: Boolean
  },
  data() {
    return {
      searchOptions: [],
      selectedCache: [],
      // Kept as plain data (rebuilt via watchers), NOT a computed: with the global
      // @vue/composition-api shim active, an Options-API computed that returns a fresh
      // array can lose its cache and hand vue-multiselect a new `:options` ref on every
      // render — its internal `filteredOptions` watcher then fires every render, and a
      // pageful of these pickers locks the tab.
      mergedOptions: [],
      // The searchable <input> is only rendered while THIS field is focused. A pageful of
      // always-searchable vue-multiselects each keep an <input>, and a password-manager
      // extension focus-pinging every one of them makes their activate()/$nextTick(focus)
      // ping-pong forever (see Call tree: flushCallbacks -> focus -> _onDocumentFocus).
      active: false,
      loading: false,
      loadingMore: false,
      nextCursor: null,
      term: '',
      searchTimer: null,
      scrollEl: null,
      onScroll: null
    }
  },
  computed: {
    selectedId() {
      const raw = this.value && this.value.length ? this.value[0] : null
      return raw != null && raw !== '' ? Number(raw) : null
    },
    selectedOption() {
      if (this.selectedId == null) return null
      return this.mergedOptions.find(o => o.id === this.selectedId) || null
    }
  },
  watch: {
    // Watch the resolved numeric id, not the `value` array prop: parents commonly bind
    // `:value="foo || []"`, which hands a fresh `[]` on every render — watching `value`
    // would re-run hydrateSelected() (and its request) on every parent re-render.
    // rebuildOptions() is called here too: this immediate handler runs during initWatch,
    // BEFORE the selectedCache/searchOptions watchers below exist, so the synchronous
    // selectedCache write inside hydrateSelected() (the initialOption path) would otherwise
    // not rebuild mergedOptions and the chip never shows.
    selectedId: { immediate: true, handler() { this.hydrateSelected(); this.rebuildOptions() } },
    selectedCache() { this.rebuildOptions() },
    searchOptions() { this.rebuildOptions() }
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer)
    this.detachScroll()
  },
  methods: {
    labelFor(h) {
      if (!h) return ''
      return h.owner_name ? `${h.owner_name}: ${h.name}` : h.name
    },
    extract(res) {
      const d = (res && res.data && res.data.data) || {}
      return { items: d.items || [], nextCursor: d.next_cursor || null }
    },
    rebuildOptions() {
      const seen = new Set(this.searchOptions.map(o => o.id))
      this.mergedOptions = [...this.selectedCache.filter(o => !seen.has(o.id)), ...this.searchOptions]
    },
    hydrateSelected() {
      const id = this.selectedId
      // Reject null / NaN / 0 / negatives — a bad id would hit /v2/horses/options with an
      // empty `ids` list, which the API can only answer with an unfiltered whole-table scan.
      if (!Number.isInteger(id) || id <= 0) {
        this.selectedCache = []
        return
      }
      if (this.selectedCache.some(o => o.id === id) || this.searchOptions.some(o => o.id === id)) return

      // Use the option the parent already resolved — no per-row API call.
      if (this.initialOption && Number(this.initialOption.id) === id) {
        this.selectedCache = [{ ...this.initialOption, id }]
        return
      }

      // Fallback: only when the selected id wasn't supplied by the parent (legacy data).
      horsesApi.options({ ids: String(id) })
        .then((res) => { this.selectedCache = this.extract(res).items })
        .catch(() => {})
    },
    onSelect(option) {
      if (option && option.id != null) {
        // Keep the picked horse so the chip survives searchOptions being cleared
        // when the field blurs / the search term resets after selection.
        this.selectedCache = [option]
        this.$emit('input', [String(option.id)])
      }
      // The selection is shown by our own chip below; the multiselect must stay a
      // plain search box. vue-multiselect 2.0.8 keeps its pick in its own
      // `internalValue` and only re-syncs it when the `:value` prop changes — and
      // ours is a static `null` — so wipe that internal state after every select.
      this.resetMultiselect()
    },
    resetMultiselect() {
      const ms = this.$refs.ms
      if (!ms) return
      ms.internalValue = []
      ms.search = ''
    },
    clear() {
      if (this.disabled) return
      // Drop every trace of the current pick: the emitted value, the hydrated chip,
      // stale search state and the multiselect's own retained selection.
      clearTimeout(this.searchTimer)
      this.term = ''
      this.searchOptions = []
      this.selectedCache = []
      this.nextCursor = null
      // mergedOptions is plain data (see note above) — rebuild it now instead of
      // waiting for the async selectedCache/searchOptions watchers, so `selectedOption`
      // (and the chip) clears in the same tick as the click.
      this.rebuildOptions()
      this.resetMultiselect()
      const ms = this.$refs.ms
      if (ms && typeof ms.deactivate === 'function') ms.deactivate()
      this.$emit('input', [])
    },
    onSearch(query) {
      this.term = query || ''
      clearTimeout(this.searchTimer)
      if (this.term.length < MIN_CHARS) {
        this.searchOptions = []
        this.nextCursor = null
        return
      }
      this.searchTimer = setTimeout(() => this.runSearch(), DEBOUNCE_MS)
    },
    runSearch() {
      this.loading = true
      horsesApi.options({ search: this.term })
        .then((res) => {
          const { items, nextCursor } = this.extract(res)
          this.searchOptions = items
          this.nextCursor = nextCursor
        })
        .catch(() => { this.searchOptions = [] })
        .finally(() => { this.loading = false })
    },
    loadMore() {
      if (!this.nextCursor || this.loadingMore || this.loading) return
      this.loadingMore = true
      horsesApi.options({ search: this.term, cursor: this.nextCursor })
        .then((res) => {
          const { items, nextCursor } = this.extract(res)
          this.searchOptions = [...this.searchOptions, ...items]
          this.nextCursor = nextCursor
        })
        .catch(() => {})
        .finally(() => { this.loadingMore = false })
    },
    onFocusIn(e) {
      if (this.active || this.disabled) return
      // Focus landing on the chip's remove button (keyboard tab, or a browser that
      // still focuses it despite @mousedown.prevent) must not activate the search.
      if (e && e.target && e.target.closest && e.target.closest('.tags-input')) return
      this.active = true
      // Re-render with searchable=true, then open the dropdown and focus the freshly
      // rendered <input> (activate() alone no-ops once isOpen is already true).
      this.$nextTick(() => {
        const ms = this.$refs.ms
        if (!ms) return
        if (!ms.isOpen) ms.activate()
        const input = ms.$refs && ms.$refs.search
        if (input && document.activeElement !== input) input.focus()
      })
    },
    onFocusOut(e) {
      // Ignore focus moving between elements inside this component.
      if (e && e.relatedTarget && this.$el.contains(e.relatedTarget)) return
      this.active = false
    },
    onOpen() {
      this.$nextTick(this.attachScroll)
    },
    onClose() {
      this.detachScroll()
    },
    // vue-multiselect has no "load more" hook — watch the dropdown's scroll container.
    attachScroll() {
      const root = this.$refs.ms && this.$refs.ms.$el
      const wrapper = root && root.querySelector('.multiselect__content-wrapper')
      if (!wrapper) return
      this.scrollEl = wrapper
      this.onScroll = () => {
        if (wrapper.scrollHeight - wrapper.scrollTop - wrapper.clientHeight < 40) this.loadMore()
      }
      wrapper.addEventListener('scroll', this.onScroll)
    },
    detachScroll() {
      if (this.scrollEl && this.onScroll) this.scrollEl.removeEventListener('scroll', this.onScroll)
      this.scrollEl = null
      this.onScroll = null
    }
  }
}
</script>

<style scoped>
/* <button> reset so it renders exactly like the legacy <i.tags-input-remove> icon
   (18x18 background image comes from _filter.scss). z-index keeps it clickable above
   the faux-disabled ::before overlay some wrappers add. */
.tags-input-remove {
  padding: 0;
  border: 0;
  background-color: transparent;
  position: relative;
  z-index: 2;
  cursor: pointer;
}

/* A long "owner: horse name" pick shouldn't force the chip wider than the field
   above it — wrap onto a second line instead of pushing the width out. Relies
   on the column itself having `min-width: 0` (set by the parent); without that
   a flex column still grows to fit unbroken text like this. */
.tags-input .badge {
  max-width: 100%;
  min-width: 0;
  align-items: flex-start;
}

.tags-input-label {
  white-space: normal;
  overflow-wrap: break-word;
  min-width: 0;
  text-align: left;
}
</style>



// WEBPACK FOOTER //
// src/components/Horses/HorseSearchSelect.vue